'use client';

import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export interface InlineEditProps extends StyledProps, UnsafeStyleProps {
  value: string | number;
  onChange?: (value: string | number) => void;
  type?: 'text' | 'number';
  /** Display formatter for read mode. */
  format?: (value: string | number) => string;
  placeholder?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  disabled?: boolean;
}

export const InlineEdit = React.forwardRef<HTMLSpanElement, InlineEditProps>(function InlineEdit(
  {
    value,
    onChange,
    type = 'text',
    format,
    placeholder = 'Click to edit',
    prefix,
    suffix,
    disabled = false,
    className,
    unsafe_style,
  },
  ref,
) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value ?? ''));
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setDraft(String(value ?? ''));
  }, [value]);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (disabled) return;
    setEditing(true);
    setDraft(String(value ?? ''));
  };

  const save = () => {
    setEditing(false);
    const v = type === 'number' ? parseFloat(draft) || 0 : draft;
    if (onChange && v !== value) onChange(v);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(String(value ?? ''));
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') save();
    else if (e.key === 'Escape') cancel();
  };

  const display = format ? format(value) : String(value ?? '');

  if (editing) {
    return (
      <span
        ref={ref}
        className={cx('j-inline-edit', className)}
        data-editing="true"
        style={unsafe_style}
      >
        {prefix && <span className="j-inline-edit-prefix">{prefix}</span>}
        <input
          ref={inputRef}
          className="j-inline-edit-input"
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={onInputKey}
        />
        {suffix && <span className="j-inline-edit-suffix">{suffix}</span>}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={cx('j-inline-edit', className)}
      style={unsafe_style}
      tabIndex={disabled ? -1 : 0}
      role="button"
      aria-label={`Edit ${display}`}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEdit();
        }
      }}
    >
      <span className="j-inline-edit-display">
        {prefix && <span className="j-inline-edit-prefix">{prefix}</span>}
        {display || <span className="j-inline-edit-placeholder">{placeholder}</span>}
        {suffix && <span className="j-inline-edit-suffix">{suffix}</span>}
      </span>
    </span>
  );
});
